import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Infinity as InfinityIcon, PackageCheck, Search } from 'lucide-react';
import type { MenuItem } from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useCategories, useMenuItems } from '../hooks/queries';
import { useToast } from '../hooks/useToast';
import {
  Button,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
} from '../components/ui';

/** Taps that cover most of a service: today's prep count, or none left. */
const QUICK_COUNTS = [0, 2, 5, 10, 20, 50];

/**
 * What is left today, for the manager who sets it before service.
 *
 * Stock lives on the menu item (see the Menu screen for everything else about
 * it), but it is the one property that changes every single day, so it gets a
 * screen of its own: tap an item, type a number, done. The figures are for the
 * floor only — nothing here reaches a guest's bill or the e-invoice.
 */
export function StockPage() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: items, isLoading, error, refetch } = useMenuItems();
  const { data: categories = [] } = useCategories();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [trackedOnly, setTrackedOnly] = useState(false);
  const [editing, setEditing] = useState<MenuItem | null>(null);
  const [draft, setDraft] = useState('0');

  const save = useMutation({
    mutationFn: (input: { id: string; trackStock: boolean; stockQuantity: number | null }) =>
      api.menuItems.update(input.id, {
        trackStock: input.trackStock,
        stockQuantity: input.trackStock ? input.stockQuantity : null,
      }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['menu-items'] });
      setEditing(null);
      toast.success(
        updated.trackStock
          ? `${updated.name} — ${updated.stockQuantity ?? 0} available.`
          : `${updated.name} is unlimited again.`,
      );
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not save that count.'),
  });

  // Out of stock first, then everything else still being counted, then the
  // unlimited items — the order a manager works down the list in.
  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    const rank = (item: MenuItem) => {
      if (!item.trackStock) return 2;
      return (item.stockQuantity ?? 0) <= 0 ? 0 : 1;
    };
    return (items ?? [])
      .filter((item) => {
        if (trackedOnly && !item.trackStock) return false;
        if (categoryFilter && item.categoryId !== categoryFilter) return false;
        if (!query) return true;
        return (
          item.name.toLowerCase().includes(query) || item.sku?.toLowerCase().includes(query) === true
        );
      })
      .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  }, [items, search, categoryFilter, trackedOnly]);

  const counts = useMemo(() => {
    const list = items ?? [];
    const tracked = list.filter((item) => item.trackStock);
    return {
      tracked: tracked.length,
      out: tracked.filter((item) => (item.stockQuantity ?? 0) <= 0).length,
    };
  }, [items]);

  function open(item: MenuItem) {
    setEditing(item);
    setDraft(item.trackStock ? String(item.stockQuantity ?? 0) : '');
  }

  const parsed = Number(draft);
  const draftValid = draft.trim() !== '' && Number.isInteger(parsed) && parsed >= 0;

  return (
    <div className="mx-auto max-w-[1400px] px-5 py-6">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-wide text-ink">In Stock</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-faint">
            Tap an item to set how many are available today. The floor sees these counts while
            taking orders, and an item that runs out can no longer be added. Internal only — stock
            never appears on a guest's bill.
          </p>
        </div>
        <div className="flex items-center gap-6">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-ink-faint">
              Counted
            </div>
            <div className="tnum mt-0.5 font-display text-2xl leading-none text-ink">
              {counts.tracked}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-ink-faint">
              Out of stock
            </div>
            <div
              className={clsx(
                'tnum mt-0.5 font-display text-2xl leading-none',
                counts.out > 0 ? 'text-danger' : 'text-ink',
              )}
            >
              {counts.out}
            </div>
          </div>
        </div>
      </header>

      <div className="mb-5 flex flex-wrap gap-3">
        <div className="relative min-w-56 flex-1">
          <Search
            size={17}
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint"
          />
          <Input
            className="pl-10"
            placeholder="Search items…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <Select
          className="w-full sm:w-56"
          value={categoryFilter}
          onChange={(event) => setCategoryFilter(event.target.value)}
        >
          <option value="">All categories</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </Select>
        <button
          type="button"
          onClick={() => setTrackedOnly((value) => !value)}
          className={clsx(
            'h-12 shrink-0 rounded-lg border px-4 text-sm font-medium transition',
            trackedOnly
              ? 'border-accent bg-accent/12 text-accent-strong'
              : 'border-line text-ink-soft hover:border-line-strong hover:text-ink',
          )}
        >
          Counted items only
        </button>
      </div>

      {isLoading ? <Spinner label="Loading the menu…" /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {!isLoading && !error && visible.length === 0 ? (
        <EmptyState
          icon={<PackageCheck size={30} />}
          title="Nothing to count"
          message={trackedOnly ? 'No item is being counted yet.' : 'Nothing matches that search.'}
        />
      ) : null}

      {visible.length > 0 ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-3">
          {visible.map((item) => {
            const out = item.trackStock && (item.stockQuantity ?? 0) <= 0;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => open(item)}
                className={clsx(
                  'flex min-h-[7rem] flex-col rounded-card border bg-surface p-4 text-left transition',
                  'hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0',
                  out ? 'border-danger/50 bg-danger/[0.06]' : 'border-line hover:border-accent/50',
                )}
              >
                <div className="text-sm font-medium leading-snug text-ink">{item.name}</div>
                {item.categoryName ? (
                  <div className="mt-0.5 text-[11px] text-ink-faint">{item.categoryName}</div>
                ) : null}

                <div className="mt-auto pt-3">
                  {item.trackStock ? (
                    <div className="tnum text-sm font-semibold text-danger">
                      {out ? '— Out of stock' : `— ${item.stockQuantity} available`}
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1.5 text-xs text-ink-faint">
                      <InfinityIcon size={13} /> Unlimited
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ) : null}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.name ?? ''}
        subtitle={
          editing?.trackStock
            ? `Currently ${editing.stockQuantity ?? 0} available`
            : 'Currently unlimited'
        }
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            {editing?.trackStock ? (
              <Button
                variant="secondary"
                loading={save.isPending && save.variables?.trackStock === false}
                onClick={() =>
                  editing &&
                  save.mutate({ id: editing.id, trackStock: false, stockQuantity: null })
                }
              >
                Unlimited
              </Button>
            ) : null}
            <Button
              variant="primary"
              size="lg"
              disabled={!draftValid}
              loading={save.isPending && save.variables?.trackStock === true}
              onClick={() =>
                editing &&
                draftValid &&
                save.mutate({ id: editing.id, trackStock: true, stockQuantity: parsed })
              }
            >
              Save
            </Button>
          </>
        }
      >
        {editing ? (
          <StockEntry
            value={draft}
            onChange={setDraft}
            onSubmit={() =>
              draftValid && save.mutate({ id: editing.id, trackStock: true, stockQuantity: parsed })
            }
          />
        ) : null}
      </Modal>
    </div>
  );
}

/** Big number, steppers and one-tap counts — a count set in a second or two. */
function StockEntry({
  value,
  onChange,
  onSubmit,
}: {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void;
}) {
  const current = Number(value);
  const step = (by: number) =>
    onChange(String(Math.max(0, (Number.isFinite(current) ? current : 0) + by)));

  // Opening the sheet should land the cursor on the number, ready to overtype.
  const [node, setNode] = useState<HTMLInputElement | null>(null);
  useEffect(() => {
    node?.select();
  }, [node]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="secondary" size="lg" className="w-14 shrink-0" onClick={() => step(-1)}>
          −
        </Button>
        <Field label="Available today" className="flex-1">
          <Input
            ref={setNode}
            type="number"
            min={0}
            inputMode="numeric"
            className="text-center text-2xl"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onSubmit();
              }
            }}
          />
        </Field>
        <Button variant="secondary" size="lg" className="w-14 shrink-0" onClick={() => step(1)}>
          +
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {QUICK_COUNTS.map((count) => (
          <button
            key={count}
            type="button"
            onClick={() => onChange(String(count))}
            className={clsx(
              'h-11 min-w-12 rounded-lg border px-3 text-sm font-semibold transition',
              Number(value) === count
                ? 'border-accent bg-accent/15 text-accent-strong'
                : 'border-line text-ink-soft hover:border-line-strong hover:text-ink',
            )}
          >
            {count === 0 ? <span className="text-danger">None</span> : count}
          </button>
        ))}
      </div>

      <p className="text-xs text-ink-faint">
        Press Enter to save. At zero the item shows as out of stock on the order screen and cannot
        be added to a bill.
      </p>
    </div>
  );
}
